import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { Loader2, Save, Send, Mail, AlertTriangle, Check } from "lucide-react";

const LS_TOKEN = "bnm-admin-token";
const FIELDS = [
  ["subject", "Subject", "input"],
  ["intro", "What they now have (intro)", "textarea"],
  ["steps", "Step-by-step instructions", "textarea"],
  ["closing", "Sign-off", "input"],
];

export default function AdminAccessEmails() {
  const [token] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState({});
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/access-templates`, auth);
      setTemplates(data.templates || []);
      setErr("");
    } catch (e) {
      setErr(e.response?.status === 401 ? "Please sign in at /admin first." : "Could not load templates.");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (!token) { setErr("Please sign in at /admin first."); setLoading(false); return; } load(); /* eslint-disable-next-line */ }, [token]);

  const setField = (type, key, val) => setTemplates((prev) => prev.map((t) => (t.type === type ? { ...t, [key]: val } : t)));

  const save = async (t) => {
    setBusy(`save-${t.type}`); setMsg((m) => ({ ...m, [t.type]: "" }));
    try {
      await axios.put(`${API}/admin/access-templates/${t.type}`,
        { subject: t.subject, intro: t.intro, steps: t.steps, closing: t.closing }, auth);
      setMsg((m) => ({ ...m, [t.type]: "Saved" }));
      setTimeout(() => setMsg((m) => ({ ...m, [t.type]: "" })), 2000);
    } catch (e) {
      setMsg((m) => ({ ...m, [t.type]: e.response?.data?.detail || "Could not save." }));
    } finally { setBusy(""); }
  };

  const sendTest = async (t) => {
    setBusy(`test-${t.type}`); setMsg((m) => ({ ...m, [t.type]: "" }));
    try {
      const { data } = await axios.post(`${API}/admin/access-templates/${t.type}/test`, {}, auth);
      setMsg((m) => ({ ...m, [t.type]: data.ok ? `Test sent to ${data.sent_to} (${data.provider}).` : (data.error || "Test could not be sent.") }));
    } catch (e) {
      setMsg((m) => ({ ...m, [t.type]: e.response?.data?.detail || "Test could not be sent." }));
    } finally { setBusy(""); }
  };

  if (err) return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-ivory-100 px-5 text-center">
      <div><p className="text-ink-soft" data-testid="access-emails-error">{err}</p><Link to="/admin" className="mt-3 inline-block text-gold-deep underline">Go to /admin</Link></div>
    </div>
  );

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(180deg,#F3EFEA 0%,#ECE7DE 100%)" }} data-testid="admin-access-emails">
      <div className="mx-auto max-w-4xl px-5 py-10 sm:px-8">
        <AdminNav />
        <h1 className="flex items-center gap-2 font-display text-3xl text-ink"><Mail className="h-7 w-7 text-gold-deep" /> Access emails</h1>
        <p className="mt-1 text-sm text-ink-muted">Edit the wording for each access type. The access code box, the button to bracednomore.online, and the Book/Vault notes are added automatically. Send yourself a test anytime.</p>

        <div className="mt-4 flex items-start gap-2 rounded-xl bg-gold/10 p-3 text-sm text-ink-soft" data-testid="access-emails-note">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold-deep" />
          <span>These send through Mailgun (mg.bracednomore.online). If Mailgun has your domain paused for list quality, tests will fail until it's reactivated — use “Clean up” in the CRM and ask Mailgun to re-enable the domain.</span>
        </div>

        {loading ? (
          <p className="mt-8 flex items-center gap-2 text-sm text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        ) : (
          <div className="mt-6 space-y-6">
            {templates.map((t) => (
              <div key={t.type} className="rounded-2xl bg-ivory-50 p-5 shadow-sm ring-1 ring-ivory-400/60" data-testid={`access-tpl-${t.type}`}>
                <h2 className="font-display text-xl text-ink">{t.label}</h2>
                <div className="mt-3 space-y-3">
                  {FIELDS.map(([key, label, kind]) => (
                    <div key={key}>
                      <label className="mb-1 block text-[11px] uppercase tracking-[0.14em] text-ink-muted">{label}</label>
                      {kind === "textarea" ? (
                        <textarea rows={key === "intro" ? 3 : 2} value={t[key] || ""} onChange={(e) => setField(t.type, key, e.target.value)}
                          data-testid={`access-tpl-${t.type}-${key}`}
                          className="w-full resize-y rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-3 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                      ) : (
                        <input value={t[key] || ""} onChange={(e) => setField(t.type, key, e.target.value)}
                          data-testid={`access-tpl-${t.type}-${key}`}
                          className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => save(t)} disabled={busy === `save-${t.type}`} data-testid={`access-tpl-${t.type}-save`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-display text-sm text-ivory-50 disabled:opacity-50">
                    {busy === `save-${t.type}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                  </button>
                  <button type="button" onClick={() => sendTest(t)} disabled={busy === `test-${t.type}`} data-testid={`access-tpl-${t.type}-test`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-4 py-2 text-sm text-ink-soft hover:text-ink disabled:opacity-50">
                    {busy === `test-${t.type}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test to me
                  </button>
                  {msg[t.type] && <span className="inline-flex items-center gap-1 text-sm text-sage-deep" data-testid={`access-tpl-${t.type}-msg`}><Check className="h-3.5 w-3.5" />{msg[t.type]}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
